import { get, omit } from "lodash";
import { toast } from "react-semantic-toasts";

import {
    INVOICE_BUILDER_UPDATE_CUSTOMER,
    INVOICE_BUILDER_SET_CUSTOMER,
    INVOICE_BUILDER_SELECT_PRODUCT,
    INVOICE_BUILDER_TOGGLE_TAXABLE,
    INVOICE_BUILDER_CHANGE_PRICE,
    INVOICE_BUILDER_CHANGE_QTY,
    INVOICE_BUILDER_ADD_PRODUCT,
    INVOICE_BUILDER_SAVED,
    INVOICE_BUILDER_SAVING,
    INVOICE_BUILDER_ERROR,
    INVOICE_BUILDER_REMOVE_ITEM,
    INVOICE_BUILDER_INVOICE_DATA,
    INVOICE_BUILDER_INVOICE_LOADING,
    INVOICE_BUILDER_CHANGE_STATUS,
    INVOICE_BUILDER_CHANGE_DATE,
    INVOICE_BUILDER_CHANGE_STAMP,
    INVOICE_BUILDER_REMOVE_STAMP_ROLL,
    INVOICE_BUILDER_BEGIN_STAMP_ROLL_EDIT,
    INVOICE_BUILDER_CANCEL_STAMP_FORM,
    INVOICE_BUILDER_CHANGE_NOTE,
    INVOICE_BUILDER_CHANGE_DELIVERY_TYPE,
    INVOICE_BUILDER_CHANGE_FULFILLMENT_NOTE,
    INVOICE_BUILDER_RESET,
    INVOICE_BUILDER_CLONING_LAST,
    INVOICE_BUILDER_CLONE,
    INVOICE_BUILDER_CREATED,
    INVOICE_BUILDER_SHOW_INLINE_EDITOR,
    INVOICE_BUILDER_HIDE_INLINE_EDITOR,
    INVOICE_BUILDER_CHANGE_INLINE_EDITOR,
    INVOICE_BUILDER_CHANGE_LOGO,
    INVOICE_BUILDER_TOGGLE_LOGO_PICKER,
    INVOICE_BUILDER_ADD_STAMP_ROLL,
    INVOICE_BUILDER_GET_ORDER,
    INVOICE_BUILDER_ADD_PRODUCTS_FROM_RECOMMENDATION,
    INVOICE_BUILDER_DUPLICATE_INVOICE,
} from "../constants";
import { makePrivateApiCall } from "utilities/dist/api";
import { loadLastStamp } from "../../stamp/warning/actions";
import {
    trackInvoiceCreateEvent,
    trackInvoiceEditEvent,
    trackSendEmailEvent,
} from "../../common/analytics";
import { createItemLog } from "../helpers";
import { store } from "../../store";
import { getTaxRate } from "utilities/dist/company/helpers";

export const resetInvoiceBuilder = () => ({
    type: INVOICE_BUILDER_RESET,
});

export const addInvoiceBuilderItemsFromRecommendation = (items = []) => ({
    type: INVOICE_BUILDER_ADD_PRODUCTS_FROM_RECOMMENDATION,
    payload: items,
});

export const setInvoiceBuilderCustomer = customer => dispatch =>
    dispatch({
        type: INVOICE_BUILDER_SET_CUSTOMER,
        payload: customer,
    });

export const updateInvoiceBuilderCustomer = customer => dispatch =>
    dispatch({
        type: INVOICE_BUILDER_UPDATE_CUSTOMER,
        payload: customer,
    });

export const removeInvoiceBuilderItem = product => ({
    type: INVOICE_BUILDER_REMOVE_ITEM,
    payload: product,
});

export const changeInvoiceBuilderStamp = stampData => ({
    type: INVOICE_BUILDER_CHANGE_STAMP,
    payload: stampData,
});

export const openInvoiceBuilderStampForm = () =>
    changeInvoiceBuilderStamp({
        from: "",
        to: "",
        roll_id: undefined,
        prefix: undefined,
        roll_from: undefined,
        roll_to: undefined,
        first_available: undefined,
        stamp_contiguous_end: undefined,
        stamps_needed: undefined,
        stamps_assignable: undefined,
        stamp_start: undefined,
        stamp_end: undefined,
        isEditing: false,
        resetStampEditBackup: true,
    });

export const beginInvoiceBuilderStampRollEdit = stampIndex => ({
    type: INVOICE_BUILDER_BEGIN_STAMP_ROLL_EDIT,
    payload: stampIndex,
});

export const cancelInvoiceBuilderStampForm = () => ({
    type: INVOICE_BUILDER_CANCEL_STAMP_FORM,
});

export const removeInvoiceBuilderStampRoll = stampIndex => ({
    type: INVOICE_BUILDER_REMOVE_STAMP_ROLL,
    payload: stampIndex,
});

export const addInvoiceBuilderStampRoll = () => ({
    type: INVOICE_BUILDER_ADD_STAMP_ROLL,
});

export const getInvoiceStampsInterval =
    (params = {}) =>
    (dispatch, getState) => {
        const { from, dsCount, reclaimableIntervals, reservedIntervals } = params;
        const isEditingFromState = getState().invoiceBuilder?.editingStamp?.isEditing;
        const isEditingFromParams = params.isEditing !== undefined;

        const isEditing = isEditingFromParams
            ? Boolean(params.isEditing)
            : isEditingFromState !== undefined
            ? Boolean(isEditingFromState)
            : false;

        return makePrivateApiCall({
            url: `/invoice/stamp/interval`,
            method: "GET",
            params: {
                stamp_start: from,
                ds_count: dsCount,
                reclaimableIntervals,
                reservedIntervals,
            },
        }).then(res => {
            const mappedStamp = {
                ...res.data,
                from: res.data.stamp_start,
                to: res.data.stamp_end,
            };

            dispatch(
                changeInvoiceBuilderStamp({
                    ...mappedStamp,
                    isEditing,
                })
            );
            return mappedStamp;
        });
    };

export const changeInvoiceBuilderStatus = status => ({
    type: INVOICE_BUILDER_CHANGE_STATUS,
    payload: status,
});

export const changeInvoiceBuilderNote = note => ({
    type: INVOICE_BUILDER_CHANGE_NOTE,
    payload: note,
});

export const changeInvoiceBuilderDeliveryType = delivery_type => ({
    type: INVOICE_BUILDER_CHANGE_DELIVERY_TYPE,
    payload: delivery_type,
});

export const changeInvoiceBuilderFulfillmentNote = fulfillment_note => ({
    type: INVOICE_BUILDER_CHANGE_FULFILLMENT_NOTE,
    payload: fulfillment_note,
});

export const changeInvoiceBuilderDate = invoiced_at => ({
    type: INVOICE_BUILDER_CHANGE_DATE,
    payload: invoiced_at,
});

export const selectInvoiceBuilderProduct = product => ({
    type: INVOICE_BUILDER_SELECT_PRODUCT,
    payload: { product, taxRate: getTaxRate(store.getState()?.auth.user.company) },
});

export const changeInvoiceBuilderQty = (qty, id) => ({
    type: INVOICE_BUILDER_CHANGE_QTY,
    payload: { id, qty, taxRate: getTaxRate(store.getState()?.auth.user.company) },
});

export const changeInvoiceBuilderPrice = (price, id) => ({
    type: INVOICE_BUILDER_CHANGE_PRICE,
    payload: { id, price, taxRate: getTaxRate(store.getState()?.auth.user.company) },
});

export const toggleInvoiceBuilderTaxable = id => ({
    type: INVOICE_BUILDER_TOGGLE_TAXABLE,
    payload: { id, taxRate: getTaxRate(store.getState()?.auth.user.company) },
});

export const addInvoiceBuilderProduct = () => ({
    type: INVOICE_BUILDER_ADD_PRODUCT,
});

export const setInvoiceBuilderError = errors => ({
    type: INVOICE_BUILDER_ERROR,
    payload: errors,
});

export const setInvoiceBuilderDuplicateInvoice = invoice => ({
    type: INVOICE_BUILDER_DUPLICATE_INVOICE,
    payload: invoice,
});

export const setInvoiceBuilderSaving = (saving = true) => ({
    type: INVOICE_BUILDER_SAVING,
    payload: saving,
});

export const setDefaultInvoiceBuilderLogo = () => (dispatch, getState) => {
    const { auth } = getState();
    // FIXME: this function is firing when invoice create page loads but for some reason auth.user.company is undefiner, need to fix
    dispatch(changeInvoiceBuilderLogo(auth.user?.company?.default_logo_id));
};

export const changeShowInvoiceBuilderLogoPicker = show => ({
    type: INVOICE_BUILDER_TOGGLE_LOGO_PICKER,
    payload: show,
});

export const changeInvoiceBuilderLogo = logo_id => ({
    type: INVOICE_BUILDER_CHANGE_LOGO,
    payload: logo_id,
});

export const createInvoiceBuilderInvoice =
    (invoice = {}) =>
    (dispatch, getState) => {
        if (getState().invoiceBuilder.isSaving) return Promise.resolve();

        dispatch(setInvoiceBuilderSaving(true));
        dispatch({
            type: INVOICE_BUILDER_DUPLICATE_INVOICE,
            payload: null,
        });
        const request = {
            url: `/invoice`,
            method: "post",
        };
        if (typeof invoice.note !== "string") invoice.note = undefined;
        trackInvoiceCreateEvent(invoice, {});

        return makePrivateApiCall({
            ...request,
            data: invoice,
        })
            .then(results => {
                trackInvoiceCreateEvent(invoice, { success: true });
                dispatch({
                    type: INVOICE_BUILDER_CREATED,
                    payload: results.data,
                });
                dispatch(loadLastStamp());
                return results.data;
            })
            .catch(err => {
                const status = get(err, "response.status", null);

                if (status === 409) {
                    dispatch(setInvoiceBuilderDuplicateInvoice(err.response.data.invoice));
                } else {
                    trackInvoiceCreateEvent(invoice, { error: true });
                    handleSavingError(dispatch, err);
                }
            });
    };

const handleSavingError = (dispatch, err) => {
    let messages = [];
    const details = get(err, "response.data.details", null);

    if (details) {
        messages = details.map(det => det.message);
    } else {
        const message = get(err, "response.data.message", null) || err.message;
        messages.push(message);
    }

    dispatch(setInvoiceBuilderError(messages));
    return false;
};

export const saveInvoiceBuilderInvoice =
    (invoice = {}) =>
    dispatch => {
        dispatch(setInvoiceBuilderSaving(true));
        const request = {
            url: `/invoice/${invoice.id}`,
            method: "put",
            data: invoice,
        };

        trackInvoiceEditEvent(invoice, {});

        return makePrivateApiCall(request)
            .then(res => {
                trackInvoiceEditEvent(invoice, { success: true });
                if (res.data) {
                    dispatch({ type: INVOICE_BUILDER_SAVED, payload: res.data });
                    dispatch(loadLastStamp());
                    return res.data;
                }
            })
            .catch(err => {
                trackInvoiceEditEvent(invoice, { error: true });
                handleSavingError(dispatch, err);
            });
    };

export const updateInvoiceStatus = (invoiceId, status) => dispatch => {
    dispatch(setInvoiceBuilderSaving(true));
    return makePrivateApiCall({
        url: `/invoice/${invoiceId}/status`,
        method: "patch",
        data: { status },
    })
        .then(() => {
            dispatch(setInvoiceBuilderSaving(false));
        })
        .catch(err => {
            handleSavingError(dispatch, err);
            throw err;
        });
};

// eslint-disable-next-line no-unused-vars
export const loadInvoiceForEdit = number => (dispatch, getState) => {
    if (number === null) {
        return dispatch({
            type: INVOICE_BUILDER_INVOICE_DATA,
            payload: null,
        });
    }

    // the number can be XXX or XXXB and buildInvoiceNumberWithBeer will return XXX or XXXB depending on
    // the beer flag so we can directly compare with the number

    //if we enable this line down below the invoice edit will work but create problem for those invoice which are created from order
    // const { invoiceGrid } = getState();
    // const fromGrid = invoiceGrid.invoices.find(inv =>
    //     isEqual(buildInvoiceNumberWithBeer(inv), number)
    // );
    //
    // if (fromGrid) {
    //     return dispatch({
    //         type: INVOICE_BUILDER_INVOICE_DATA,
    //         payload: fromGrid,
    //     });
    // }

    dispatch({
        type: INVOICE_BUILDER_INVOICE_LOADING,
        payload: true,
    });
    return makePrivateApiCall({
        url: `/invoice/${number}`,
    })
        .then(res => {
            if (res.data) {
                if (res.data.orders.length) {
                    const items = createItemLog(res.data.items);
                    dispatch(
                        cloneInvoice({
                            items,
                            order_number: res?.data?.orders[0]?.order_number,
                        })
                    );
                }
                return dispatch({
                    type: INVOICE_BUILDER_INVOICE_DATA,
                    payload: res.data,
                });
            }
        })
        .catch(err => {
            const message = get(err, "response.data.message", null) || err.message;
            dispatch(setInvoiceBuilderError([message]));
            return false;
        });
};

export const cloneInvoice = invoice => ({
    type: INVOICE_BUILDER_CLONE,
    payload: invoice,
});

export const cloneLastInvoiceBuilder =
    (customer = {}) =>
    dispatch => {
        dispatch({ type: INVOICE_BUILDER_CLONING_LAST, payload: true });

        return makePrivateApiCall({
            url: "/invoice/last",
            params: { customerId: customer.id },
        })
            .then(res => {
                dispatch({ type: INVOICE_BUILDER_CLONING_LAST, payload: false });
                if (res.data) {
                    return dispatch(cloneInvoice(res.data));
                } else {
                    toast({
                        type: "warning",
                        description: "Sorry you have no previous invoices for this customer",
                        title: "No invoice found",
                    });
                }
            })
            .catch(err => {
                toast({
                    type: "error",
                    description: err.message,
                    title: "Error Replicating Last Invoice",
                });
            });
    };
export const getOrderProductForInvoice = (number, defaultStatus) => dispatch => {
    dispatch({ type: INVOICE_BUILDER_GET_ORDER, payload: true });

    return makePrivateApiCall({
        url: `/order/${number}`,
    })
        .then(res => {
            dispatch({ type: INVOICE_BUILDER_GET_ORDER, payload: false });
            if (res.data) {
                const { modifiedOrderItems, order } = res.data;
                if (modifiedOrderItems?.length) {
                    order.orderItems = modifiedOrderItems;
                }
                const orderItems = createItemLog(order?.orderItems);
                const orderNotes = Array.isArray(order.note) ? order.note : [];

                return dispatch(
                    cloneInvoice({
                        ...omit(order, ["note"]),
                        status: defaultStatus,
                        items: orderItems,
                        customer: order.customerUser.customer,
                        delivery_type: order.delivery_type ?? "pickup",
                        fulfillment_note: "",
                        orderNotes,
                        order_delivery_type: order.delivery_type ?? null,
                    })
                );
            } else {
                toast({
                    type: "warning",
                    description: "Sorry you have no previous invoices for this customer",
                    title: "No invoice found",
                });
            }
        })
        .catch(err => {
            toast({
                type: "error",
                description: err.message,
                title: "Error Creating Invoice From Order",
            });
        });
};
export const showInvoiceBuilderInlineEditor = (item, inlineEditorFields) => ({
    type: INVOICE_BUILDER_SHOW_INLINE_EDITOR,
    payload: { item, inlineEditorFields },
});

export const hideInvoiceBuilderInlineEditor = field => ({
    type: INVOICE_BUILDER_HIDE_INLINE_EDITOR,
    payload: field,
});

export const changeInvoiceBuilderInlineEditor = (field, value) => ({
    type: INVOICE_BUILDER_CHANGE_INLINE_EDITOR,
    payload: { field, value, taxRate: getTaxRate(store.getState()?.auth.user.company) },
});

export const sendInvoiceViaEmail = ({ emails, invoiceNumber, notes, beer }) => {
    trackSendEmailEvent(emails, { success: true });
    return makePrivateApiCall({
        url: `/invoice/${invoiceNumber}/email`,
        data: { emails, notes, beer },
        method: "post",
    });
};
